import React, { useEffect, useState, createContext, useContext } from 'react';
import { auth, db, isFirebaseEnabled } from '../lib/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, sendPasswordResetEmail, updateProfile, User } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { notifications } from '../lib/notifications';
interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: {
    displayName?: string;
    photoURL?: string;
  }) => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Mock user for local-only mode
const createMockUser = (email: string, name: string): any => ({
  uid: `mock-${Date.now()}`,
  email,
  displayName: name,
  photoURL: `https://api.dicebear.com/7.x/avataaars/svg?seed=${email}`
});
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!isFirebaseEnabled || !auth) {
      // Local-only mode - check localStorage for mock user
      const savedUser = localStorage.getItem('questzen_mock_user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setUser(user);
      // Create user document if it doesn't exist
      if (user && db) {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) {
          await setDoc(userRef, {
            email: user.email,
            displayName: user.displayName || 'QuestZen User',
            photoURL: user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.uid}`,
            createdAt: new Date().toISOString(),
            xp: 0,
            level: 1,
            streak: 0,
            completedGoals: 0,
            focusSessions: 0,
            totalFocusMinutes: 0,
            achievements: []
          });
        }
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);
  const login = async (email: string, password: string) => {
    if (!isFirebaseEnabled || !auth) {
      // Local-only mode
      const mockUser = createMockUser(email, 'QuestZen User');
      setUser(mockUser);
      localStorage.setItem('questzen_mock_user', JSON.stringify(mockUser));
      notifications.success('Welcome back!', 'Running in local mode');
      return;
    }
    try {
      await signInWithEmailAndPassword(auth, email, password);
      notifications.success('Welcome back!', 'Successfully logged in');
    } catch (error: any) {
      notifications.error('Login failed', error.message);
      throw error;
    }
  };
  const signup = async (name: string, email: string, password: string) => {
    if (!isFirebaseEnabled || !auth) {
      // Local-only mode
      const mockUser = createMockUser(email, name);
      setUser(mockUser);
      localStorage.setItem('questzen_mock_user', JSON.stringify(mockUser));
      notifications.welcomeEmail(name, email);
      return;
    }
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Update profile with name
      await updateProfile(result.user, {
        displayName: name,
        photoURL: `https://api.dicebear.com/7.x/avataaars/svg?seed=${result.user.uid}`
      });
      // Create user document
      if (db) {
        await setDoc(doc(db, 'users', result.user.uid), {
          email,
          displayName: name,
          photoURL: `https://api.dicebear.com/7.x/avataaars/svg?seed=${result.user.uid}`,
          createdAt: new Date().toISOString(),
          xp: 0,
          level: 1,
          streak: 0,
          completedGoals: 0,
          focusSessions: 0,
          totalFocusMinutes: 0,
          achievements: []
        });
      }
      // Send welcome email notification
      notifications.welcomeEmail(name, email);
    } catch (error: any) {
      notifications.error('Signup failed', error.message);
      throw error;
    }
  };
  const loginWithGoogle = async () => {
    if (!isFirebaseEnabled || !auth) {
      notifications.error('Google login unavailable', 'Please configure Firebase to use Google login');
      throw new Error('Firebase not configured');
    }
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      // Check if this is a new user
      if (db) {
        const userRef = doc(db, 'users', result.user.uid);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) {
          // New user - send welcome email
          notifications.welcomeEmail(result.user.displayName || 'QuestZen User', result.user.email || '');
        } else {
          notifications.success('Welcome!', 'Successfully logged in with Google');
        }
      }
    } catch (error: any) {
      notifications.error('Google login failed', error.message);
      throw error;
    }
  };
  const logout = async () => {
    if (!isFirebaseEnabled || !auth) {
      // Local-only mode
      setUser(null);
      localStorage.removeItem('questzen_mock_user');
      notifications.info('Goodbye!', 'Successfully logged out');
      return;
    }
    try {
      await signOut(auth);
      notifications.info('Goodbye!', 'Successfully logged out');
    } catch (error: any) {
      notifications.error('Logout failed', error.message);
      throw error;
    }
  };
  const resetPassword = async (email: string) => {
    if (!isFirebaseEnabled || !auth) {
      notifications.error('Password reset unavailable', 'Please configure Firebase to use password reset');
      throw new Error('Firebase not configured');
    }
    try {
      await sendPasswordResetEmail(auth, email);
      notifications.success('Email sent!', 'Check your inbox for password reset instructions');
    } catch (error: any) {
      notifications.error('Reset failed', error.message);
      throw error;
    }
  };
  const updateUserProfile = async (data: {
    displayName?: string;
    photoURL?: string;
  }) => {
    if (!user) throw new Error('No user logged in');
    if (!isFirebaseEnabled || !auth) {
      // Local-only mode
      const updatedUser = {
        ...user,
        ...data
      };
      setUser(updatedUser);
      localStorage.setItem('questzen_mock_user', JSON.stringify(updatedUser));
      notifications.profileUpdated();
      return;
    }
    try {
      await updateProfile(user, data);
      // Update Firestore
      if (db) {
        await setDoc(doc(db, 'users', user.uid), data, {
          merge: true
        });
      }
      notifications.profileUpdated();
    } catch (error: any) {
      notifications.error('Update failed', error.message);
      throw error;
    }
  };
  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    signup,
    loginWithGoogle,
    logout,
    resetPassword,
    updateUserProfile
  };
  return <AuthContext.Provider value={value} data-id="element-601">
      {!loading && children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}